import analysisService from '../../platforms/aws/services/analysisService';
import { getModelById } from './bedrockModels';
import { runtimeDeprecationService } from './runtimeDeprecationService';

class AIAnalysisService {
  constructor() {
    this.onProgress = null;
    this.onStatusUpdate = null;
  }

  // Set progress callback
  setProgressCallback(callback) {
    this.onProgress = callback;
  }

  // Set status update callback
  setStatusUpdateCallback(callback) {
    this.onStatusUpdate = callback;
  }

  // Extract Lambda functions data for analysis (optimized for token usage)
  async extractLambdaData(exportedData, region) {
    const lambdaData = {
      summary: {
        totalFunctions: 0,
        regions: new Set(),
        runtimes: new Map(),
        functionsWithInvocations: 0,
        functionsWithErrors: 0,
        deprecatedFunctions: 0,
        unusedFunctions: 0, // Total unused functions (any "None in the last..." text)
        unusedFunctionsByPeriod: new Map(), // Breakdown by period (e.g., "None in the last 12 months": 56)
        runtimeDeprecationBreakdown: new Map(),
        logRetentionBreakdown: new Map(),
        functionsWithUnknownLogRetention: 0,
        functionsWithShortLogRetention: 0, // < 30 days
        functionsWithLongLogRetention: 0,  // >= 30 days
        functionsWithNeverExpire: 0, // Never Expire - medium priority issue
        functionsWithLogGroupNotFound: 0, // Log Group Not Found - low priority issue
        errorRateBreakdown: {
          lowError: 0,    // 0-10%
          mediumError: 0, // 10-50% 
          highError: 0    // >50%
        }
      }
    };
    
    console.log('Starting Lambda data extraction for AI analysis...');

    // Lambda functions are global/regional resources, so we only need to process them once
    // Find the first instance that has functions data
    let functionsData = null;
    let sourceInstanceData = null;
    
    for (const [instanceId, instanceData] of Object.entries(exportedData)) {
      if (instanceData.functions && Array.isArray(instanceData.functions) && instanceData.functions.length > 0) {
        functionsData = instanceData.functions;
        sourceInstanceData = instanceData;
        console.log(`Using Lambda functions from instance ${instanceId} (${functionsData.length} functions found)`);
        break;
      }
    }
    
    if (functionsData && sourceInstanceData) {
        for (const func of functionsData) {
          // Extract only analysis-relevant fields to minimize token usage
          const optimizedFunc = {
            functionName: func.FunctionName,
            runtime: func.Runtime,
            timeout: func.Timeout,
            memorySize: func.MemorySize,
            lastModified: func.LastModified,
            handler: func.Handler,
            region: region
          };

          // Add performance metrics (using same field names as Excel mapper)
          optimizedFunc.invocations30Days = func.Invocations30Days || 0;
          optimizedFunc.errors30Days = func.Errors30Days || 0;

          // Add last invocation info (using same field name as Excel mapper)
          optimizedFunc.lastInvocation = func.LastInvocation || null;
          
          // Add last invocation display (contextual info from Excel mapper)
          if (func.LastInvocationDisplay) {
            optimizedFunc.lastInvocationDisplay = func.LastInvocationDisplay;
          }

          // Calculate runtime deprecation status (same logic as Excel mapper)
          let blockFunctionCreate = 'Not Collected';
          let blockFunctionUpdate = 'Not Collected';
          
          if (func.Runtime) {
            try {
              const runtimeStatus = await runtimeDeprecationService.getRuntimeStatus(func.Runtime);
              blockFunctionCreate = runtimeStatus.blockFunctionCreate;
              blockFunctionUpdate = runtimeStatus.blockFunctionUpdate;
              console.log(`Function ${func.FunctionName} (${func.Runtime}): create=${blockFunctionCreate}, update=${blockFunctionUpdate}`);
            } catch (error) {
              console.warn(`Failed to get runtime status for ${func.FunctionName} (${func.Runtime}):`, error);
              blockFunctionCreate = 'Unknown';
              blockFunctionUpdate = 'Unknown';
            }
          }
          
          optimizedFunc.blockFunctionCreate = blockFunctionCreate;
          optimizedFunc.blockFunctionUpdate = blockFunctionUpdate;

          // Add log retention info (using same field names as Excel mapper)
          optimizedFunc.logRetention = func.LogGroupRetention || 'Unknown';
          optimizedFunc.logRetentionDays = func.LogGroupRetentionDays || null;

          // Add associated flows (for Connect integration analysis)
          if (func.AssociatedFlows || func._associatedFlows) {
            optimizedFunc.associatedFlows = func.AssociatedFlows || func._associatedFlows;
          }

          // Calculate months since last invocation for unused function detection
          if (optimizedFunc.lastInvocation) {
            const lastInvocationDate = new Date(optimizedFunc.lastInvocation);
            const now = new Date();
            const monthsSinceLastInvocation = Math.floor((now - lastInvocationDate) / (1000 * 60 * 60 * 24 * 30));
            optimizedFunc.monthsSinceLastInvocation = monthsSinceLastInvocation;
          } else {
            // If no last invocation data, calculate based on lastModified
            const lastModifiedDate = new Date(func.LastModified);
            const now = new Date();
            const monthsSinceLastModified = Math.floor((now - lastModifiedDate) / (1000 * 60 * 60 * 24 * 30));
            optimizedFunc.monthsSinceLastModified = monthsSinceLastModified;
          }

          // Calculate error rate 
          if (optimizedFunc.errors30Days !== undefined && optimizedFunc.errors30Days > 0) {
            if (optimizedFunc.invocations30Days > 0) {
              optimizedFunc.errorRate30Days = (optimizedFunc.errors30Days / optimizedFunc.invocations30Days * 100).toFixed(2);
            } else {
              // Functions with errors but no invocations = 100% error rate
              optimizedFunc.errorRate30Days = "100.00";
            }
          }
          
          // Add flag for functions with errors
          optimizedFunc.hasErrors30Days = (optimizedFunc.errors30Days || 0) > 0;
          
          // Add flag for deprecated runtime (for easy filtering)
          optimizedFunc.isRuntimeDeprecated = (blockFunctionCreate === 'Deprecated' || 
                                               blockFunctionUpdate === 'Deprecated');

          // Don't store individual functions, just update summary statistics
          
          // Update summary with enhanced metrics
          lambdaData.summary.totalFunctions++;
          lambdaData.summary.regions.add(region);
          
          const runtimeCount = lambdaData.summary.runtimes.get(func.Runtime) || 0;
          lambdaData.summary.runtimes.set(func.Runtime, runtimeCount + 1);
          
          // Track additional summary metrics
          if (optimizedFunc.invocations30Days > 0) {
            lambdaData.summary.functionsWithInvocations++;
          }
          if (optimizedFunc.hasErrors30Days) {
            lambdaData.summary.functionsWithErrors++;
          }
          if (optimizedFunc.isRuntimeDeprecated) {
            lambdaData.summary.deprecatedFunctions++;
            
            // Track runtime deprecation breakdown
            const runtimeKey = `${func.Runtime} (${blockFunctionCreate === 'Deprecated' ? 'Create' : ''}${blockFunctionCreate === 'Deprecated' && blockFunctionUpdate === 'Deprecated' ? '&' : ''}${blockFunctionUpdate === 'Deprecated' ? 'Update' : ''})`;
            const currentCount = lambdaData.summary.runtimeDeprecationBreakdown.get(runtimeKey) || 0;
            lambdaData.summary.runtimeDeprecationBreakdown.set(runtimeKey, currentCount + 1);
          }
          
          // Track unused functions based ONLY on LastInvocationDisplay parsing
          const lastInvocationDisplay = optimizedFunc.lastInvocationDisplay;
          let isUnusedFunction = false;
          
          if (lastInvocationDisplay) {
            // Check for "None in the last..." patterns
            if (lastInvocationDisplay.includes('None in the last')) {
              isUnusedFunction = true;
            }
            // Check for other patterns that might indicate unused functions
            else if (lastInvocationDisplay.toLowerCase().includes('never') || 
                     lastInvocationDisplay.toLowerCase().includes('no invocations') ||
                     lastInvocationDisplay === 'Unknown' ||
                     lastInvocationDisplay === 'Not Collected') {
              isUnusedFunction = true;
            }
          } else {
            // If no LastInvocationDisplay, check if function has 0 invocations (might be unused)
            if (optimizedFunc.invocations30Days === 0) {
              // Use "No LastInvocationDisplay" as the bucket
              optimizedFunc.lastInvocationDisplay = 'No LastInvocationDisplay (0 invocations)';
              isUnusedFunction = true;
            }
          }
          
          if (isUnusedFunction) {
            // This is an unused function - increment total count
            lambdaData.summary.unusedFunctions++;
            
            // Track by specific period bucket from LastInvocationDisplay
            const bucketKey = optimizedFunc.lastInvocationDisplay || 'Unknown Pattern';
            const currentCount = lambdaData.summary.unusedFunctionsByPeriod.get(bucketKey) || 0;
            lambdaData.summary.unusedFunctionsByPeriod.set(bucketKey, currentCount + 1);
          }
          
          // Track error rate breakdown - only for functions that actually have errors
          if (optimizedFunc.hasErrors30Days && optimizedFunc.errorRate30Days) {
            const errorRate = parseFloat(optimizedFunc.errorRate30Days);
            if (errorRate <= 10) {
              lambdaData.summary.errorRateBreakdown.lowError++;
            } else if (errorRate <= 50) {
              lambdaData.summary.errorRateBreakdown.mediumError++;
            } else {
              lambdaData.summary.errorRateBreakdown.highError++;
            }
          }
          
          // Track log retention breakdown
          const logRetention = optimizedFunc.logRetention;
          const logRetentionDays = optimizedFunc.logRetentionDays;
          
          if (logRetention === 'Never Expire') {
            lambdaData.summary.functionsWithNeverExpire++; // Medium priority issue
          } else if (logRetention === 'Log Group Not Found') {
            lambdaData.summary.functionsWithLogGroupNotFound++; // Low priority issue
          } else if (logRetention === 'Unknown' || logRetention === 'Not Collected') {
            lambdaData.summary.functionsWithUnknownLogRetention++;
          } else if (logRetentionDays !== null && logRetentionDays < 30) {
            lambdaData.summary.functionsWithShortLogRetention++;
          } else if (logRetentionDays !== null && logRetentionDays >= 30) {
            lambdaData.summary.functionsWithLongLogRetention++;
          } else {
            // Fallback for other cases
            lambdaData.summary.functionsWithUnknownLogRetention++;
          }
          
          // Track log retention period breakdown
          const retentionKey = logRetention === 'Never Expire' ? 'Never Expire' : 
                              (logRetentionDays ? `${logRetentionDays} days` : logRetention);
          const retentionCount = lambdaData.summary.logRetentionBreakdown.get(retentionKey) || 0;
          lambdaData.summary.logRetentionBreakdown.set(retentionKey, retentionCount + 1);
          
          // Log key data points for validation
          if (lambdaData.summary.totalFunctions <= 3) { // Log first 3 functions for validation
            console.log(`Lambda ${func.FunctionName}: invocations=${optimizedFunc.invocations30Days}, errors=${optimizedFunc.errors30Days}, logRetention=${optimizedFunc.logRetention}, lastInvocationDisplay=${optimizedFunc.lastInvocationDisplay}`);
          }
        }
    } else {
      console.log('No Lambda functions found in any instance for AI analysis');
    }

    // Convert Sets and Maps to serializable format
    lambdaData.summary.regions = Array.from(lambdaData.summary.regions);
    lambdaData.summary.runtimes = Object.fromEntries(lambdaData.summary.runtimes);
    lambdaData.summary.runtimeDeprecationBreakdown = Object.fromEntries(lambdaData.summary.runtimeDeprecationBreakdown);
    lambdaData.summary.logRetentionBreakdown = Object.fromEntries(lambdaData.summary.logRetentionBreakdown);
    lambdaData.summary.unusedFunctionsByPeriod = Object.fromEntries(lambdaData.summary.unusedFunctionsByPeriod);
    
    console.log(`Lambda AI Analysis Summary: ${lambdaData.summary.totalFunctions} functions, ${lambdaData.summary.functionsWithInvocations} with invocations, ${lambdaData.summary.functionsWithErrors} with errors, ${lambdaData.summary.deprecatedFunctions} deprecated, ${lambdaData.summary.unusedFunctions} unused total`);
    console.log(`Unused Functions Breakdown:`, lambdaData.summary.unusedFunctionsByPeriod);
    console.log(`Runtime Deprecation Breakdown:`, lambdaData.summary.runtimeDeprecationBreakdown);
    console.log(`Error Rate Breakdown:`, lambdaData.summary.errorRateBreakdown);
    console.log(`Log Retention Breakdown:`, lambdaData.summary.logRetentionBreakdown);
    console.log(`Log Retention Summary: ${lambdaData.summary.functionsWithUnknownLogRetention} unknown, ${lambdaData.summary.functionsWithShortLogRetention} short-term, ${lambdaData.summary.functionsWithLongLogRetention} long-term, ${lambdaData.summary.functionsWithNeverExpire} never expire, ${lambdaData.summary.functionsWithLogGroupNotFound} log group not found`);

    return lambdaData;
  }

  // Extract Lex V2 bots data for analysis (optimized for token usage)
  extractLexV2Data(exportedData, region) {
    const lexData = {
      bots: [],
      summary: {
        totalBots: 0,
        totalIntents: 0,
        totalSlots: 0,
        botsWithAnalytics: 0,
        unusedBots: 0,
        regions: new Set()
      }
    };

    // Use Map to deduplicate bots by botId across multiple instances
    const processedBots = new Map();

    // Iterate through all instances/regions
    Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
      if (instanceData.lexBotV2 && Array.isArray(instanceData.lexBotV2)) {
        instanceData.lexBotV2.forEach(bot => {
          // Skip if we've already processed this bot (deduplication)
          if (processedBots.has(bot.botId)) {
            console.log(`Skipping duplicate bot: ${bot.botName} (${bot.botId}) from instance ${instanceId}`);
            return;
          }
          
          // Mark this bot as processed
          processedBots.set(bot.botId, true);
          // Extract only analysis-relevant fields to minimize token usage
          const optimizedBot = {
            botName: bot.botName,
            botId: bot.botId,
            description: bot.description,
            botStatus: bot.botStatus,
            creationDateTime: bot.creationDateTime,
            lastUpdatedDateTime: bot.lastUpdatedDateTime,
            region: region
          };

          // Add conversation analytics if available with calculated metrics
          // Check both _conversationAnalytics (with underscore) and conversationAnalytics (without underscore)
          const analytics = bot.conversationAnalytics || bot._conversationAnalytics;
          if (analytics) {
            optimizedBot.conversationAnalytics = {
              totalConversations: analytics.totalConversations || 0,
              droppedConversations: analytics.droppedConversations || 0,
              failedConversations: analytics.failedConversations || 0,
              detectedUtterances: analytics.detectedUtterances || 0,
              missedUtterances: analytics.missedUtterances || 0
            };
            
            // Calculate failure rates for easier analysis
            if (analytics.totalConversations > 0) {
              const totalIssues = (analytics.failedConversations || 0) + (analytics.droppedConversations || 0);
              optimizedBot.conversationAnalytics.failureRate = ((totalIssues / analytics.totalConversations) * 100).toFixed(2);
            }
            
            // Calculate missed utterance rate
            const totalUtterances = (analytics.detectedUtterances || 0) + (analytics.missedUtterances || 0);
            if (totalUtterances > 0) {
              optimizedBot.conversationAnalytics.missedUtteranceRate = (((analytics.missedUtterances || 0) / totalUtterances) * 100).toFixed(2);
            }
            
            // Flag if bot appears unused (no conversations)
            optimizedBot.conversationAnalytics.isUnused = (analytics.totalConversations || 0) === 0;
            
            console.log(`Bot ${bot.botName} has analytics: total=${analytics.totalConversations}, failed=${analytics.failedConversations}, dropped=${analytics.droppedConversations}`);
          } else {
            // Mark as no analytics available
            optimizedBot.conversationAnalytics = null;
            optimizedBot.noAnalyticsData = true;
            console.log(`Bot ${bot.botName} has no analytics data available`);
          }

          // Add associated flows (for Connect integration analysis)
          if (bot._associatedFlows) {
            optimizedBot.associatedFlows = bot._associatedFlows;
          }

          // Add simplified locale information (counts and key metrics only)
          if (bot.botLocales && Array.isArray(bot.botLocales)) {
            optimizedBot.locales = bot.botLocales.map(locale => ({
              localeId: locale.localeId,
              nluIntentConfidenceThreshold: locale.nluIntentConfidenceThreshold,
              intentCount: locale.intents ? locale.intents.length : 0,
              slotTypeCount: locale.slotTypes ? locale.slotTypes.length : 0,
              // Include sample intent names for analysis context (first 5 only)
              sampleIntents: locale.intents ? locale.intents.slice(0, 5).map(intent => intent.intentName) : []
            }));
          }

          lexData.bots.push(optimizedBot);
          
          console.log(`Added unique bot: ${bot.botName} (${bot.botId}) from instance ${instanceId}`);
          
          // Update summary
          lexData.summary.totalBots++;
          lexData.summary.regions.add(region);
          
          // Track analytics availability
          if (optimizedBot.conversationAnalytics) {
            lexData.summary.botsWithAnalytics++;
            if (optimizedBot.conversationAnalytics.isUnused) {
              lexData.summary.unusedBots++;
            }
          }
          
          // Count intents and slots from locales
          if (bot.botLocales) {
            bot.botLocales.forEach(locale => {
              if (locale.intents) {
                lexData.summary.totalIntents += locale.intents.length;
              }
              if (locale.slotTypes) {
                lexData.summary.totalSlots += locale.slotTypes.length;
              }
            });
          }
        });
      }
    });

    // Convert Sets to serializable format
    lexData.summary.regions = Array.from(lexData.summary.regions);
    
    console.log(`Processed ${lexData.summary.totalBots} unique Lex V2 bots (${processedBots.size} deduplicated)`);

    return lexData;
  }


  extractConnectData(exportedData, region) {
    const connectData = {
      instances: [],
      summary: {
        totalInstances: 0,
        totalFlows: 0,
        totalFlowsWithErrors: 0,
        regions: new Set()
      }
    };

    console.log('Starting Connect data extraction for AI analysis...');

    // Process each Connect instance
    Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
      // Check if this instance has contact flows or contact flow modules data
      const hasContactFlows = instanceData.contactFlows && Array.isArray(instanceData.contactFlows);
      const hasContactFlowModules = instanceData.contactFlowModules && Array.isArray(instanceData.contactFlowModules);
      
      if (hasContactFlows || hasContactFlowModules) {
        // Combine contact flows and contact flow modules for analysis
        const contactFlows = instanceData.contactFlows || [];
        const contactFlowModules = instanceData.contactFlowModules || [];
        const allFlowItems = [...contactFlows, ...contactFlowModules];
        
        // Get instance name (use correct path from instanceConfig.instanceDetails.InstanceAlias)
        const instanceName = instanceData.instanceConfig?.instanceDetails?.InstanceAlias || 
                            instanceData.instanceDetails?.InstanceAlias || 
                            instanceData.instanceAlias ||
                            instanceId;

        console.log(`Processing Connect instance: ${instanceName} (${instanceId}) with ${contactFlows.length} contact flows and ${contactFlowModules.length} contact flow modules`);

        // Analyze all flow items (flows + modules) for this instance
        let flowsWithErrors = 0;
        allFlowItems.forEach(flowItem => {
          if ((flowItem.ContactFlowErrors30Days || 0) > 0) {
            flowsWithErrors++;
          }
        });

        // Store instance analysis data
        const instanceAnalysis = {
          instanceId: instanceId,
          instanceName: instanceName,
          totalFlows: allFlowItems.length,
          flowsWithErrors: flowsWithErrors,
          region: region
        };

        connectData.instances.push(instanceAnalysis);
        
        // Update summary statistics
        connectData.summary.totalInstances++;
        connectData.summary.totalFlows += allFlowItems.length;
        connectData.summary.totalFlowsWithErrors += flowsWithErrors;
        connectData.summary.regions.add(region);

        console.log(`Instance ${instanceName}: ${allFlowItems.length} total items (${contactFlows.length} flows + ${contactFlowModules.length} modules), ${flowsWithErrors} with errors`);
      } else {
        console.log(`Skipping instance ${instanceId} - no contact flows or contact flow modules data found`);
      }
    });

    // Convert Sets to serializable format
    connectData.summary.regions = Array.from(connectData.summary.regions);
    
    console.log(`Connect AI Analysis Summary: ${connectData.summary.totalInstances} instances, ${connectData.summary.totalFlows} total flows, ${connectData.summary.totalFlowsWithErrors} flows with errors`);

    return connectData;
  }

  // Extract Trusted Advisor data for analysis (optimized for token usage)
  extractTrustedAdvisorData(exportedData, region) {
    const trustedAdvisorData = {
      summary: {
        totalRecommendations: 0,
        statusBreakdown: {
          actionRecommended: 0,
          investigationRecommended: 0,
          ok: 0,
          other: 0
        },
        categoryBreakdown: {},
        potentialMonthlySavings: {
          totalAmount: 0,
          contributingRecommendations: 0,
          formattedAmount: '$0.00'
        },
        highPriorityActions: 0,
        mediumPriorityActions: 0,
        regions: new Set()
      }
    };

    console.log('Starting Trusted Advisor data extraction for AI analysis...');

    // Trusted Advisor recommendations are account-wide, so we only need to process them once
    // Find the first instance that has Trusted Advisor data
    let recommendationsData = null;
    let sourceInstanceData = null;
    
    for (const [instanceId, instanceData] of Object.entries(exportedData)) {
      if (instanceData.recommendations && Array.isArray(instanceData.recommendations) && instanceData.recommendations.length > 0) {
        recommendationsData = instanceData.recommendations;
        sourceInstanceData = instanceData;
        console.log(`Using Trusted Advisor recommendations from instance ${instanceId} (${recommendationsData.length} recommendations found)`);
        break;
      }
    }
    
    if (recommendationsData && sourceInstanceData) {
      for (const recommendation of recommendationsData) {
        // Update total count
        trustedAdvisorData.summary.totalRecommendations++;
        trustedAdvisorData.summary.regions.add(region);

        // Process status breakdown
        const status = recommendation.Status || 'Unknown';
        if (status === 'Action Recommended') {
          trustedAdvisorData.summary.statusBreakdown.actionRecommended++;
          trustedAdvisorData.summary.highPriorityActions++;
        } else if (status === 'Investigation Recommended') {
          trustedAdvisorData.summary.statusBreakdown.investigationRecommended++;
          trustedAdvisorData.summary.mediumPriorityActions++;
        } else if (status === 'OK') {
          trustedAdvisorData.summary.statusBreakdown.ok++;
        } else {
          trustedAdvisorData.summary.statusBreakdown.other++;
        }

        // Process category breakdown
        const category = recommendation.Category || 'Unknown';
        const currentCategoryCount = trustedAdvisorData.summary.categoryBreakdown[category] || 0;
        trustedAdvisorData.summary.categoryBreakdown[category] = currentCategoryCount + 1;

        // Process potential monthly savings
        if (recommendation.PotentialMonthlySavings && recommendation.PotentialMonthlySavings !== '') {
          // Parse the savings amount (format: "$123.45")
          const savingsMatch = recommendation.PotentialMonthlySavings.match(/\$?(\d+\.?\d*)/);
          if (savingsMatch) {
            const savingsAmount = parseFloat(savingsMatch[1]);
            if (!isNaN(savingsAmount) && savingsAmount > 0) {
              trustedAdvisorData.summary.potentialMonthlySavings.totalAmount += savingsAmount;
              trustedAdvisorData.summary.potentialMonthlySavings.contributingRecommendations++;
            }
          }
        }

        // Log key data points for validation (first 3 recommendations)
        if (trustedAdvisorData.summary.totalRecommendations <= 3) {
          console.log(`Trusted Advisor ${recommendation.Name}: status=${status}, category=${category}, savings=${recommendation.PotentialMonthlySavings || 'N/A'}`);
        }
      }

      // Format the total savings amount
      trustedAdvisorData.summary.potentialMonthlySavings.formattedAmount = 
        `$${trustedAdvisorData.summary.potentialMonthlySavings.totalAmount.toFixed(2)}`;

    } else {
      console.log('No Trusted Advisor recommendations found in any instance for AI analysis');
    }

    // Convert Sets to serializable format
    trustedAdvisorData.summary.regions = Array.from(trustedAdvisorData.summary.regions);
    
    console.log(`Trusted Advisor AI Analysis Summary: ${trustedAdvisorData.summary.totalRecommendations} recommendations, ${trustedAdvisorData.summary.highPriorityActions} high priority, ${trustedAdvisorData.summary.mediumPriorityActions} medium priority`);
    console.log(`Status Breakdown:`, trustedAdvisorData.summary.statusBreakdown);
    console.log(`Category Breakdown:`, trustedAdvisorData.summary.categoryBreakdown);
    console.log(`Potential Monthly Savings: ${trustedAdvisorData.summary.potentialMonthlySavings.formattedAmount} from ${trustedAdvisorData.summary.potentialMonthlySavings.contributingRecommendations} recommendations`);

    return trustedAdvisorData;
  }

  // Perform AI analysis for a specific service
  async analyzeService(serviceName, data, modelId, accessToken = null) {
    try {
      this.updateStatus(`Analyzing ${serviceName} data with AI...`);

      // Get model configuration for maxTokens and temperature
      const model = getModelById(modelId);
      if (!model) {
        throw new Error(`Unknown model ID: ${modelId}`);
      }

      // Call the new API Gateway-based analysis service
      const result = await analysisService.analyzeService(
        serviceName,
        data,
        modelId,
        {
          maxTokens: model.maxTokens,
          temperature: model.temperature || 0.1,
          accessToken
        }
      );

      if (!result.success) {
        throw new Error(result.error || 'Analysis failed');
      }

      // Add metadata to the result
      const analysisResult = result.analysis;
      analysisResult._metadata = {
        serviceName,
        analysisDate: new Date().toISOString(),
        dataSize: JSON.stringify(data).length,
        ...result.metadata
      };

      this.updateStatus(`AI analysis completed for ${serviceName}`);
      return analysisResult;

    } catch (error) {
      console.error(`Error analyzing ${serviceName}:`, error);
      throw new Error(`Failed to analyze ${serviceName}: ${error.message}`);
    }
  }

  // Perform comprehensive AI analysis for selected services
  // Note: credentials and region are no longer needed as analysis is now performed via API Gateway
  async performAnalysis(exportedData, selectedServices, modelId, onProgress = null, accessToken = null) {
    try {
      this.setProgressCallback(onProgress);

      const results = {
        analysisDate: new Date().toISOString(),
        services: {},
        summary: {
          totalServicesAnalyzed: selectedServices.length,
          analysisCompleted: false,
          errors: []
        }
      };

      // Extract region from exportedData for metadata purposes
      let region = 'us-east-1'; // default
      try {
        const firstInstanceId = Object.keys(exportedData)[0];
        if (firstInstanceId && exportedData[firstInstanceId]?.instanceConfig?.instanceDetails?.InstanceArn) {
          const arn = exportedData[firstInstanceId].instanceConfig.instanceDetails.InstanceArn;
          region = arn.split(':')[3] || 'us-east-1';
        }
      } catch (error) {
        console.warn('Unable to extract region from exportedData, using default:', error);
      }

      let completedServices = 0;
      const totalServices = selectedServices.length;

      for (const serviceName of selectedServices) {
        try {
          this.updateProgress(completedServices / totalServices * 100, `Analyzing ${serviceName}...`);

          let serviceData;

          // Extract data based on service type
          switch (serviceName) {
            case 'lambda':
              serviceData = await this.extractLambdaData(exportedData, region);
              break;
            case 'lexV2':
              serviceData = this.extractLexV2Data(exportedData, region);
              break;
            case 'connect':
              serviceData = this.extractConnectData(exportedData, region);
              break;
            case 'trustedAdvisor':
              serviceData = this.extractTrustedAdvisorData(exportedData, region);
              break;
            default:
              throw new Error(`Unsupported service for AI analysis: ${serviceName}`);
          }

          // Skip analysis if no data found
          if (serviceName === 'lambda' && serviceData.summary.totalFunctions === 0) {
            results.summary.errors.push(`No Lambda functions found for analysis`);
            completedServices++;
            continue;
          }
          
          // Note: lexV2 with no bots proceeds with analysis and shows "No bots found" in its
          // section instead of being treated as an analysis error
          
          if (serviceName === 'connect' && serviceData.instances.length === 0) {
            results.summary.errors.push(`No Amazon Connect instances with contact flows found for analysis`);
            completedServices++;
            continue;
          }

          if (serviceName === 'trustedAdvisor' && serviceData.summary.totalRecommendations === 0) {
            results.summary.errors.push(`No Trusted Advisor recommendations found for analysis`);
            completedServices++;
            continue;
          }

          // Special case: Trusted Advisor uses manual summarization, no AI analysis needed
          if (serviceName === 'trustedAdvisor') {
            results.services[serviceName] = {
              data: serviceData,
              analysis: null // No AI analysis needed - manual summarization in Word document
            };
            
            completedServices++;
            this.updateProgress(completedServices / totalServices * 100, `Completed ${serviceName} analysis`);
            continue; // Skip AI analysis step
          }

          // Perform AI analysis for other services (via API Gateway)
          const analysisResult = await this.analyzeService(
            serviceName,
            serviceData,
            modelId,
            accessToken
          );

          results.services[serviceName] = {
            data: serviceData,
            analysis: analysisResult
          };

          completedServices++;
          this.updateProgress(completedServices / totalServices * 100, `Completed ${serviceName} analysis`);

        } catch (error) {
          console.error(`Error analyzing ${serviceName}:`, error);
          results.summary.errors.push(`${serviceName}: ${error.message}`);
          completedServices++;
        }
      }

      results.summary.analysisCompleted = true;
      this.updateProgress(100, 'AI analysis completed');
      
      return results;

    } catch (error) {
      console.error('Error performing AI analysis:', error);
      throw new Error(`AI analysis failed: ${error.message}`);
    }
  }

  // Helper methods
  updateProgress(percentage, message) {
    if (this.onProgress) {
      this.onProgress(percentage, message);
    }
  }

  updateStatus(message) {
    if (this.onStatusUpdate) {
      this.onStatusUpdate(message);
    }
  }
}

// Export singleton instance
export const aiAnalysisService = new AIAnalysisService();